import apiClient from '../client';

export interface CertificationInterest {
  id: string;
  freelancer_profile_id: string;
  is_interested: boolean;
  timezone: string | null;
  preferred_schedule: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface CertificationCohort {
  id: string;
  name: string;
  organizer_profile_id: string;
  description: string | null;
  max_members: number;
  status: 'forming' | 'full' | 'submitted' | 'completed';
  created_at: string;
  updated_at: string;
  certification_cohort_members?: { count: number }[];
  freelancer_profiles?: { display_name: string; profile_picture_url: string | null } | null;
  members?: CohortMember[];
}

export interface CohortMember {
  id: string;
  cohort_id: string;
  freelancer_profile_id: string;
  role: 'organizer' | 'member';
  joined_at: string;
  freelancer_profiles: {
    id: string;
    display_name: string;
    headline: string | null;
    profile_picture_url: string | null;
    location: string | null;
    skills: string[] | null;
  };
}

export interface InterestedFreelancer {
  id: string;
  freelancer_profile_id: string;
  is_interested: boolean;
  timezone: string | null;
  preferred_schedule: string | null;
  note: string | null;
  freelancer_profiles: {
    id: string;
    user_id: string;
    display_name: string;
    headline: string | null;
    bio: string | null;
    profile_picture_url: string | null;
    location: string | null;
    skills: string[] | null;
    hourly_rate_cents: number | null;
  };
}

export interface CertificationAdminStats {
  totalInterested: number;
  totalCohorts: number;
  forming: number;
  full: number;
  submitted: number;
  completed: number;
}

export const certificationService = {
  // Interest
  getMyInterest: async (): Promise<CertificationInterest | null> => {
    const result = await apiClient.get<CertificationInterest | null>('/certification/interest/me');
    return result as unknown as CertificationInterest | null;
  },

  upsertMyInterest: async (data: {
    is_interested: boolean;
    timezone?: string | null;
    preferred_schedule?: string | null;
    note?: string | null;
  }): Promise<CertificationInterest> => {
    const result = await apiClient.post<CertificationInterest>('/certification/interest/me', data);
    return result as unknown as CertificationInterest;
  },

  getInterestedFreelancers: async (filters?: {
    skills?: string;
    timezone?: string;
    location?: string;
  }): Promise<InterestedFreelancer[]> => {
    const params = new URLSearchParams();
    if (filters?.skills) params.append('skills', filters.skills);
    if (filters?.timezone) params.append('timezone', filters.timezone);
    if (filters?.location) params.append('location', filters.location);
    const qs = params.toString();
    const result = await apiClient.get<InterestedFreelancer[]>(`/certification/interest/freelancers${qs ? `?${qs}` : ''}`);
    return result as unknown as InterestedFreelancer[];
  },

  // Cohorts
  createCohort: async (data: {
    name: string;
    description?: string | null;
    max_members?: number;
  }): Promise<CertificationCohort> => {
    const result = await apiClient.post<CertificationCohort>('/certification/cohorts', data);
    return result as unknown as CertificationCohort;
  },

  listCohorts: async (status?: string): Promise<CertificationCohort[]> => {
    const qs = status ? `?status=${status}` : '';
    const result = await apiClient.get<CertificationCohort[]>(`/certification/cohorts${qs}`);
    return result as unknown as CertificationCohort[];
  },

  getCohort: async (id: string): Promise<CertificationCohort> => {
    const result = await apiClient.get<CertificationCohort>(`/certification/cohorts/${id}`);
    return result as unknown as CertificationCohort;
  },

  joinCohort: async (id: string): Promise<CohortMember> => {
    const result = await apiClient.post<CohortMember>(`/certification/cohorts/${id}/join`);
    return result as unknown as CohortMember;
  },

  leaveCohort: async (id: string): Promise<void> => {
    await apiClient.post(`/certification/cohorts/${id}/leave`);
  },

  getMyCohorts: async (): Promise<any[]> => {
    const result = await apiClient.get<any[]>('/certification/cohorts/mine');
    return result as unknown as any[];
  },

  // Admin
  getAdminCohorts: async (): Promise<{ cohorts: CertificationCohort[]; stats: CertificationAdminStats }> => {
    const result = await apiClient.get<{ cohorts: CertificationCohort[]; stats: CertificationAdminStats }>('/certification/admin/cohorts');
    return result as unknown as { cohorts: CertificationCohort[]; stats: CertificationAdminStats };
  },

  updateCohortStatus: async (id: string, status: string): Promise<CertificationCohort> => {
    const result = await apiClient.patch<CertificationCohort>(`/certification/admin/cohorts/${id}`, { status });
    return result as unknown as CertificationCohort;
  },

  getAdminInterested: async (): Promise<InterestedFreelancer[]> => {
    const result = await apiClient.get<InterestedFreelancer[]>('/certification/admin/interested');
    return result as unknown as InterestedFreelancer[];
  },

  adminToggleInterest: async (profileId: string, is_interested: boolean): Promise<any> => {
    const result = await apiClient.post(`/certification/admin/interest/toggle?profileId=${profileId}`, { is_interested });
    return result;
  },
};
